<template>
  <el-collapse-item :title="activityLabel" :name="newActivityIndex">
    <template #title>
      <div class="activity-preview activity-preview--edit-mode">
        <div class="activity-preview__heading">
          <activity-icon
            v-if="activityData.activity.activityId"
            class="activity-preview__icon"
            :size="32"
            :activity="activityData.activity.activityId"
          />
          <span class="activity-preview__title">
            {{ activityLabel }}
          </span>
        </div>
      </div>
    </template>

    <div
      :class="[
        'activity activity--edit-mode',
        { 'activity--active': isActive },
      ]"
    >
      <div class="activity__kind">
        <div class="activity__subtitle">Activity</div>

        <el-form
          :model="activityFormData"
          :rules="activityFormRules"
          ref="activityForm"
          label-position="top"
        >
          <div class="row">
            <div class="col col-xs-12 col-md-6">
              <el-form-item
                label="Activity type"
                prop="activityId"
                :error="activityFormErrors.activityId"
              >
                <el-select
                  class="wide"
                  v-model="activityFormData.activityId"
                  placeholder="Choose from the list"
                  :disabled="isActivityTypeChangeDisabled"
                >
                  <el-option
                    v-for="type in activityTypeDescriptions"
                    :key="type.value"
                    :label="type.description"
                    :value="type.value"
                  >
                  </el-option>
                </el-select>
              </el-form-item>
            </div>

            <div class="col col-xs-12 col-md-6">
              <el-form-item
                label="Level"
                prop="level"
                :error="activityFormErrors.level"
              >
                <el-select
                  class="wide"
                  v-model="activityFormData.level"
                  placeholder="Choose from the list"
                >
                  <el-option
                    v-for="level in descriptions.activityCVLevel"
                    :key="level.value"
                    :label="level.description"
                    :value="level.value"
                  >
                  </el-option>
                </el-select>
              </el-form-item>
            </div>

            <div class="col col-xs-12">
              <el-form-item
                prop="story"
                :error="activityFormErrors.story"
                label="Story"
              >
                <el-input
                  type="textarea"
                  :rows="4"
                  v-model="activityFormData.story"
                  placeholder="Tell about your experience in this activity"
                ></el-input>
              </el-form-item>
            </div>
          </div>

          <div class="activity__buttons">
            <el-button
              type="primary"
              @click="validateactivityFormData"
              :disabled="isButtonDisabled('save-activity')"
            >
              Save changes
            </el-button>
          </div>
        </el-form>
      </div>

      <edit-achievements
        v-if="activityData.activityCVId"
        :achievements-data="
          fullActivityData ? fullActivityData.achievements : null
        "
        :activity-c-v-id="activityData.activityCVId"
        :activity-id="activityFormData.activityId"
        @update-user-data="handleUpdateUserData"
      />

      <edit-certificates
        v-if="activityData.activityCVId"
        :certificates-data="
          fullActivityData ? fullActivityData.certificates : null
        "
        :activity-c-v-id="activityData.activityCVId"
        :activity-id="activityFormData.activityId"
        @update-user-data="handleUpdateUserData"
      />

      <edit-skills
        v-if="activityData.activityCVId"
        :skills-data="fullActivityData ? fullActivityData.skills : null"
        :activity-c-v-id="activityData.activityCVId"
        :activity-id="activityFormData.activityId"
        @update-user-data="handleUpdateUserData"
      />

      <confirmation-dialog
        content="Are you sure to delete this activity?"
        @on-confirm="handleRemoveActivity"
        confirm-button-type="danger"
        confirm-button-text="Delete"
      >
        <el-button
          class="activity__remove-button"
          plain
          type="danger"
          :disabled="isButtonDisabled('delete-activity')"
        >
          <el-icon><Delete/></el-icon>
          Delete CV for&nbsp;
          <strong>
            {{ activityLabel }}
          </strong>
        </el-button>
      </confirmation-dialog>
    </div>
  </el-collapse-item>
</template>

<script>
import {
  getCurrentUserFullActivityCV,
  updateActivity,
  removeActivity,
  addActivity,
} from '@/api/activity.js';

import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import EditAchievements from '@/components/profile/EditAchievements.vue';
import EditCertificates from '@/components/profile/EditCertificates.vue';
import EditSkills from '@/components/profile/EditSkills.vue';
import ActivityIcon from '@/components/common/ActivityIcon.vue';

import { parseErrorsByFields } from '@/utils/index.js';
import { showMessage } from '@/utils/messages.js';
import { disableButton } from '@/mixins/disableButton.js';

export default {
  name: 'Activity',
  props: {
    activityData: {
      type: Object,
      required: false,
    },
    isActive: {
      type: Boolean,
      required: false,
    },
    newActivityIndex: {
      type: Number,
      required: true,
    },
    availableActivityTypeDescriptions: {
      type: Array,
      required: false,
    },
  },
  mixins: [disableButton],
  components: {
    ConfirmationDialog,
    EditAchievements,
    EditCertificates,
    EditSkills,
    ActivityIcon,
  },
  data() {
    return {
      fullActivityData: null,
      activityFormData: {
        activityId: null,
        level: null,
        story: null,
      },
      activityFormErrors: {
        activityId: null,
        level: null,
        story: null,
      },
      activityFormRules: {
        activityId: [
          {
            required: true,
            message: 'Please choose type of activity',
            trigger: 'change',
          },
        ],
        level: [
          {
            required: true,
            message: 'Please choose your activity level',
            trigger: 'change',
          },
        ],
        story: [
          {
            required: false,
            message: '',
            trigger: 'blur',
          },
        ],
      },
    };
  },
  computed: {
    descriptions() {
      return this.$store.state.descriptions;
    },
    activityLabel() {
      const { activityCVId, activity } = this.activityData;
      return activityCVId ? activity.name : 'New activity';
    },
    isFormFilled() {
      const { type, level } = this.activityFormData;
      return type !== null && level !== null;
    },
    isActivityTypeChangeDisabled() {
      const { activityData } = this;
      return activityData.activity.activityId ? true : false;
    },
    activityTypeDescriptions() {
      const {
        activityData,
        descriptions,
        availableActivityTypeDescriptions,
      } = this;

      if (activityData.activityCVId) {
        return descriptions.activityType;
      } else {
        return availableActivityTypeDescriptions
          ? availableActivityTypeDescriptions
          : descriptions.activityType;
      }
    },
  },
  methods: {
    parseErrorsByFields,
    showMessage,
    async fetchFullActivityData() {
      const { activityCVId } = this.activityData;
      const { data, success } = await getCurrentUserFullActivityCV({
        activityCVId,
      });

      if (success) {
        this.fullActivityData = data;
      }
    },
    async requestUpdateActivity() {
      this.disableButton('save-activity');
      const { activityId, level, story } = this.activityFormData;
      const { activityCVId } = this.activityData;

      const { success, errors } = await updateActivity({
        activityCVId,
        activityId,
        level,
        story,
      });
      this.enableButton('save-activity');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Activity has been updated',
        });
        this.handleUpdateUserData();
      } else if (errors) {
        this.activityFormErrors = [
          ...this.activityFormErrors,
          ...this.parseErrorsByFields(errors),
        ];
      }
    },
    async requestRemoveActivity() {
      this.disableButton('delete-activity');
      const { activityCVId } = this.activityData;
      const { success, errors } = await removeActivity({ activityCVId });
      this.enableButton('delete-activity');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Activity has been removed',
        });
        this.$emit('update-data');
        this.fullActivityData = null;
      }
    },
    async requestAddActivity() {
      this.disableButton('save-activity');
      const { activityId, level, story } = this.activityFormData;
      const { success, errors } = await addActivity({
        activityId,
        level,
        story,
      });
      this.enableButton('save-activity');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'New activity has been added',
        });
        this.handleUpdateUserData();
      } else if (errors) {
        this.activityFormErrors = [
          ...this.activityFormErrors,
          ...this.parseErrorsByFields(errors),
        ];
      }
    },
    setActivityData() {
      const { isActive, activityData } = this;
      const { level, story, activity } = this.activityData;

      this.activityFormData.activityId = activity ? activity.activityId : null;
      this.activityFormData.level = level ? level.value : null;
      this.activityFormData.story = story ? story : null;

      if (isActive && activityData.activityCVId) {
        this.fetchFullActivityData();
      }
    },
    validateactivityFormData() {
      const {
        isFormFilled,
        availableActivityTypeDescriptions,
        activityFormData,
      } = this;
      const { activityCVId } = this.activityData;

      if (
        availableActivityTypeDescriptions.length === 0 &&
        !activityFormData.activityId
      ) {
        this.showMessage({
          type: 'error',
          message: 'You added all types of activity to your CV',
        });
      } else {
        this.$refs.activityForm.validate();

        if (isFormFilled) {
          if (activityCVId) {
            this.requestUpdateActivity();
          } else {
            this.requestAddActivity();
          }
        }
      }
    },
    handleUpdateUserData() {
      this.$emit('update-data');
    },
    async handleRemoveActivity() {
      const { activityCVId } = this.activityData;
      const { requestRemoveActivity, newActivityIndex } = this;

      if (activityCVId) {
        await requestRemoveActivity();
        this.$emit('remove-activity', null);
      } else {
        this.$emit('remove-activity', newActivityIndex);
      }
    },
  },
  created() {
    const { isActive, activityData } = this;

    if (activityData.activityCVId) {
      this.setActivityData();
    }

    if (isActive && activityData.activityCVId) {
      this.fetchFullActivityData();
    }
  },
  watch: {
    isActive(newVal) {
      const { fullActivityData, activityData } = this;
      if (newVal && !fullActivityData && activityData.activityCVId) {
        this.fetchFullActivityData();
      }
    },
    activityData(newVal) {
      if (newVal.activityCVId) {
        this.setActivityData();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/shared/_globals.scss';

.activity-preview {
  $this: &;

  &--edit-mode {
    #{$this}__title {
      margin-bottom: 0;
    }
  }
}

.activity {
  $this: &;

  &--edit-mode {
    #{$this}__buttons {
      text-align: right;
    }
    #{$this}__remove-button {
      margin-right: var(--global-dimension-size-200);
    }
  }
}
</style>
